import { FC } from 'react';
import { Box, Stack } from '@mui/material';

import { SkeletonBlock } from '@mopla/ui';

/** Placeholder lines standing in for the explanatory paragraph. */
const DESCRIPTION_LINES = ['100%', '100%', '100%', '55%'];

/** A label + value pair (customer email / ticket ID). */
const FieldPair: FC<{ labelW: number; valueW: number | string }> = ({
	labelW,
	valueW,
}) => (
	<Stack gap={0.5} sx={{ py: 0.5 }}>
		<SkeletonBlock w={labelW} h={14} r={4} />
		<SkeletonBlock w={valueW} h={20} r={4} />
	</Stack>
);

/**
 * Loading skeleton for the chipcard request screen. Mirrors the resolved layout
 * — wordmark strip, description, the two fields (with operator icon), and the
 * contact block — so no layout shift occurs when the ticket resolves. Rendered
 * inside DTicketPageLayout.
 */
export const ChipcardRequestSkeleton: FC = () => (
	<>
		{/* Operator wordmark strip (matches MplOperatorWordmark reserve). */}
		<Box
			sx={{
				pt: '8px',
				height: 60,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
			}}
		>
			<SkeletonBlock w="80%" h={54} r={8} />
		</Box>

		<Stack
			gap={3}
			sx={{ width: '100%', pt: 2 }}
			data-testid="chipcard-request-skeleton"
		>
			{/* Description paragraph */}
			<Stack gap={1}>
				{DESCRIPTION_LINES.map((w, i) => (
					<SkeletonBlock key={i} w={w} h={16} r={4} />
				))}
			</Stack>

			{/* Customer email */}
			<FieldPair labelW={120} valueW={220} />

			{/* Ticket ID + operator (bus) icon */}
			<Stack direction="row" gap={2} alignItems="center" sx={{ py: 0.5 }}>
				<Stack gap={0.5} sx={{ flex: 1, minWidth: 0 }}>
					<SkeletonBlock w={80} h={14} r={4} />
					<SkeletonBlock w={180} h={20} r={4} />
				</Stack>
				<SkeletonBlock w={48} h={48} r={24} />
			</Stack>

			{/* Contact block */}
			<Stack gap={1}>
				<SkeletonBlock w={90} h={12} r={4} />
				<SkeletonBlock w="70%" h={18} r={4} />
				<SkeletonBlock w="55%" h={18} r={4} />
				<SkeletonBlock w="45%" h={18} r={4} />
				<SkeletonBlock w={160} h={18} r={4} />
				<SkeletonBlock w={220} h={18} r={4} />
			</Stack>
		</Stack>
	</>
);
